/**
 * Raising an alert from application code.
 *
 * This replaces the legacy AlertContext for the one thing it did that the rule engine does not:
 * let a subsystem announce an event directly. Rules answer "does this record match?" — but a new
 * trading call, a call-tracker outcome or a batch of generated strategies is an event, not a
 * property of any row, which is exactly why those legacy presets could never be expressed as rules.
 *
 * Everything raised here lands in the same delivery path as a rule match, so there is one popup
 * stack, one sound, and one place to change how alerts feel.
 */

import { emitAlert, playAlertSound, showBrowserNotification } from './alertRuntime';

export interface RaiseOptions {
  /** Play the alert sound. Defaults to true; pass false for low-value background notices. */
  sound?: boolean;
  /** Also post an OS notification when permission has already been granted. */
  browser?: boolean;
  /** Groups repeats: two events with the same key inside the popup window read as one. */
  key?: string;
}

let seq = 0;

/** Announce an event through the alert channels. Safe to call from anywhere, never throws. */
export function raiseAlert(title: string, message: string, options: RaiseOptions = {}): void {
  try {
    seq += 1;
    const key = options.key ?? `${title}|${message}`;
    if (options.sound !== false) playAlertSound();
    if (options.browser) showBrowserNotification(title, message);
    emitAlert({
      id: `raised_${Date.now().toString(36)}_${seq}`,
      ruleId: 'raised',
      ruleName: title,
      sourceId: 'event',
      recordKey: key,
      summary: message,
      at: Date.now(),
    });
  } catch {
    // An alert failing to display must never take down the thing that raised it.
  }
}

// ── legacy raise-points, kept so callers read the same as before ──────────────

/** Shape the old AlertContext accepted; only the parts that were ever displayed are used. */
export interface LegacyAlertLike {
  title?: string;
  message?: string;
  symbol?: string;
  underlying?: string;
  /** Callers pass the old Alert record wholesale; the rest is ignored rather than rejected. */
  [key: string]: unknown;
}

export function addAlert(alert: LegacyAlertLike): void {
  raiseAlert(alert.title || 'Alert', alert.message || alert.symbol || alert.underlying || '');
}

export function addTradingCallAlert(callType: 'new' | 'update', callData: unknown): void {
  const d = (callData ?? {}) as Record<string, unknown>;
  const symbol = String(d.symbol ?? d.Symbol ?? d.underlying ?? '').trim();
  const detail = [symbol, d.strike ? `${d.strike}` : '', String(d.direction ?? d.side ?? '')]
    .filter(Boolean)
    .join(' ');
  raiseAlert(
    callType === 'new' ? 'New trading call' : 'Trading call updated',
    detail || 'A trading call changed',
    { browser: true },
  );
}

export function addCallTrackerAlert(
  result: 'profit' | 'loss' | 'did_not_work',
  callData: unknown,
): void {
  const d = (callData ?? {}) as Record<string, unknown>;
  const symbol = String(d.symbol ?? d.Symbol ?? d.underlying ?? '').trim();
  const label =
    result === 'profit' ? 'Call closed in profit' : result === 'loss' ? 'Call closed at a loss' : 'Call did not work out';
  raiseAlert(label, symbol || 'Trading call closed', { browser: true });
}

export function addGeneratorStrategiesAlert(payload: {
  count: number;
  symbols: string[];
  ar?: boolean;
}): void {
  const { count, symbols, ar } = payload;
  const list = symbols.slice(0, 6).join(', ');
  raiseAlert(
    ar ? 'استراتيجيات جديدة' : 'New strategy ideas',
    ar ? `${count} استراتيجية: ${list}` : `${count} generated for ${list}`,
    { browser: true },
  );
}
